/**
 * A barra que chama a mensagem pronta (H05c).
 *
 * ── Quando o comando está ativo ──────────────────────────────────────────
 *
 * Só quando a `/` é o PRIMEIRO caractere do campo. É a regra mais restrita que
 * atende o pedido, e ela existe para não estragar o que já funciona: "manda o
 * link https://loja.com/ofertas" tem duas barras no meio, e nenhuma delas é
 * comando. Abrir a lista ali seria uma caixa aparecendo por cima do que a
 * pessoa está escrevendo, no meio de uma frase.
 *
 * ── O termo é TUDO o que vem depois da barra ─────────────────────────────
 *
 * Inclusive os espaços: os títulos têm espaço ("Saudação da manhã"), e cortar
 * no primeiro faria `/saudação da` procurar por "saudação" e mostrar a lista
 * inteira de novo, como se o que foi digitado depois não contasse.
 *
 * ── O comando casa só o TÍTULO ───────────────────────────────────────────
 *
 * A caixa de mensagens prontas busca em título e texto, e ali isso ajuda: quem
 * procura lembra do conteúdo, e o campo se chama "Buscar" com "título ou texto"
 * escrito embaixo.
 *
 * O comando é outra coisa. `/ajuda` é a pessoa dizendo o NOME do que quer, não
 * procurando. Casar o conteúdo faz `/ajuda` trazer junto uma mensagem chamada
 * "Questionamento" só porque o texto dela termina em "…te ajudar com mais
 * facilidade?" — e a lista devolve o que ninguém pediu, na hora em que o
 * cliente está esperando.
 *
 * ── Escolher SUBSTITUI o campo ───────────────────────────────────────────
 *
 * O campo inteiro é o comando — `/ajuda` não é texto que a pessoa quer enviar.
 * Por isso a escolha troca tudo pelo texto da mensagem, e não insere no cursor
 * como o emoji faz: inserir deixaria `/ajuda` grudado no começo da resposta, e
 * alguém acabaria mandando isso ao cliente.
 */

import { normalizar } from '../ui/dialogo-de-modelos';

export interface ComandoLido {
  /** A lista deve aparecer? */
  readonly ativo: boolean;
  /** O que foi digitado depois da barra — pode ser vazio, e aí lista tudo. */
  readonly termo: string;
}

export function lerComando(texto: string): ComandoLido {
  if (!texto.startsWith('/')) return { ativo: false, termo: '' };
  return { ativo: true, termo: texto.slice(1) };
}

/**
 * Move o destaque sem sair da lista.
 *
 * Circula: do último volta ao primeiro. Numa lista curta — e ela é curta —,
 * parar na ponta faz a pessoa apertar a seta duas vezes achando que travou.
 */
export function proximoDestaque(atual: number, delta: number, total: number): number {
  if (total === 0) return 0;
  return (atual + delta + total) % total;
}

/** O mínimo que o filtro precisa saber de uma mensagem pronta. */
export interface ProntaFiltravel {
  readonly titulo: string;
}

/**
 * As que casam com o termo pelo TÍTULO.
 *
 * Sem acento e sem caixa, e todos os termos em qualquer ordem — as mesmas três
 * regras da busca da caixa, aplicadas a um campo só. `/manha saudacao` acha
 * "Saudação da manhã", porque quem digita rápido inverte.
 */
export function filtrarPorTitulo<T extends ProntaFiltravel>(
  lista: readonly T[],
  termo: string,
): readonly T[] {
  const alvo = normalizar(termo.trim());
  if (alvo === '') return lista;

  const termos = alvo.split(/\s+/);
  return lista.filter((item) => {
    const titulo = normalizar(item.titulo);
    return termos.every((t) => titulo.includes(t));
  });
}
